import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import {
  LucideChevronDown,
  LucideChevronUp,
  LucideCornerDownLeft,
  LucideCornerDownRight,
  LucideHeart,
  LucidePencil,
  LucideTrash2,
} from '@lucide/angular';
import { TranslocoService } from '@ngneat/transloco';
import { Comment, FlatReplyDetails, UiComment } from '../../models/comment/comment.model';
import { COMMENT_DATA_SERVICE, CommentDataService } from '../../services/comment/comment-data.service';
import { LikesDialogComponent, LikesDialogData } from '../likes-dialog/likes-dialog.component';

@Component({
  selector: 'ptl-comment-item',
  templateUrl: './comment-item.component.html',
  styleUrls: ['./comment-item.component.scss'],
})
export class CommentItemComponent implements OnInit {
  @Input() comment!: UiComment;
  @Input() momentId!: string;
  @Input() isTopLevel = false;
  @Input() currentUserId!: string;

  @Output() commentUpdated = new EventEmitter<Comment>();
  @Output() commentDeleted = new EventEmitter<string>();
  @Output() commentLiked = new EventEmitter<{ commentId: string; isLiked: boolean }>();
  @Output() replyCreated = new EventEmitter<{ reply: Comment; parentCommentId: string; replyingToAuthorName?: string }>();

  protected readonly LucideHeart = LucideHeart;
  protected readonly LucideCornerDownLeft = LucideCornerDownLeft;
  protected readonly LucideCornerDownRight = LucideCornerDownRight;
  protected readonly LucidePencil = LucidePencil;
  protected readonly LucideTrash2 = LucideTrash2;
  protected readonly LucideChevronDown = LucideChevronDown;
  protected readonly LucideChevronUp = LucideChevronUp;

  isCurrentUserAuthor = false;
  isLoading = false;
  isLoadingReplies = false;
  hasMoreReplies = false;

  private repliesPage = 0;
  private readonly repliesPageSize = 5;

  constructor(
    @Inject(COMMENT_DATA_SERVICE) private commentService: CommentDataService,
    public translocoService: TranslocoService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.isCurrentUserAuthor = this.currentUserId === this.comment.authorId;
  }

  getInitials(name: string): string {
    return name
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((word) => word[0]?.toUpperCase() ?? '')
      .join('');
  }

  getAvatarColorClass(name: string): string {
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return `avatar-color-${hash % 6}`;
  }

  onLikeToggle(): void {
    if (this.isLoading) return;

    this.isLoading = true;
    const isLiked = !this.comment.likes.liked;

    const request$ = isLiked ? this.commentService.likeComment(this.comment.id) : this.commentService.unlikeComment(this.comment.id);

    request$.subscribe({
      next: (result) => {
        if (result.isSuccess) {
          this.commentLiked.emit({ commentId: this.comment.id, isLiked });
        } else {
          console.error('Error toggling like:', result.error);
        }
      },
      error: (error) => {
        console.error('Error toggling like:', error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  startEdit(): void {
    this.comment.isEditing = true;
  }

  cancelEdit(): void {
    this.comment.isEditing = false;
  }

  onDelete(): void {
    if (this.isLoading) return;

    this.commentService.deleteComment(this.comment.id).subscribe({
      next: (result) => {
        if (result.isSuccess) {
          this.commentDeleted.emit(this.comment.id);
        } else {
          console.error('Error deleting comment:', result.error);
        }
      },
      error: (error) => {
        console.error('Error deleting comment:', error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  startReply(): void {
    this.comment.isReplying = true;
  }

  onReplyCreated(comment: Comment): void {
    this.comment.isReplying = false;
    const replyingToAuthorName = this.isTopLevel ? undefined : this.comment.authorName;
    this.replyCreated.emit({ reply: comment, parentCommentId: this.comment.id, replyingToAuthorName });
  }

  onEditSaved(content: string): void {
    if (content.trim() === this.comment.content) {
      this.cancelEdit();
      return;
    }

    this.isLoading = true;
    this.commentService.updateComment(this.comment.id, { content }).subscribe({
      next: (result) => {
        if (result.isSuccess) {
          this.comment.content = result.value.content;
          this.comment.updatedAt = result.value.updatedAt;
          this.comment.isEditing = false;
          this.commentUpdated.emit(result.value);
        } else {
          console.error('Error updating comment:', result.error);
        }
      },
      error: (error) => {
        console.error('Error updating comment:', error);
      },
      complete: () => {
        this.isLoading = false;
      },
    });
  }

  showLikesDialog(event: Event): void {
    event.stopPropagation();

    if (this.comment.likes.totalLikes > 0) {
      this.commentService.getCommentLikes(this.comment.id).subscribe({
        next: (result) => {
          if (result.isSuccess) {
            const dialogData: LikesDialogData = {
              commentContent: this.comment.content,
              likedBy: result.value,
              totalLikes: this.comment.likes.totalLikes,
            };

            this.dialog.open(LikesDialogComponent, {
              width: '500px',
              maxHeight: '600px',
              data: dialogData,
              panelClass: ['ptl-mat-dialog', 'likes-dialog'],
              backdropClass: 'dialog-backdrop',
            });
          }
        },
        error: (error) => {
          console.error('Error loading comment likes:', error);
        },
      });
    }
  }

  toggleReplies(): void {
    this.comment.isExpanded = !this.comment.isExpanded;

    const repliesNotLoadedYet = !this.comment.flatReplies || this.comment.flatReplies.length === 0;
    if (this.comment.isExpanded && repliesNotLoadedYet && this.comment.repliesCount > 0) {
      this.loadReplies({ append: false });
    }
  }

  loadMoreReplies(): void {
    this.loadReplies({ append: true });
  }

  get totalRepliesCount(): number {
    return this.comment.totalRepliesCount || this.comment.repliesCount;
  }

  trackByReplyId(index: number, reply: UiComment): string {
    return reply.id;
  }

  private toUiComment(reply: FlatReplyDetails): UiComment {
    return {
      id: reply.id,
      content: reply.content,
      authorId: reply.authorId,
      authorName: reply.authorName,
      authorAvatarUrl: reply.authorAvatarUrl,
      createdAt: reply.createdAt,
      updatedAt: reply.updatedAt,
      momentId: reply.momentId,
      parentCommentId: reply.parentCommentId,
      repliesCount: 0,
      likes: reply.likes,
      isExpanded: false,
      isEditing: false,
      isReplying: false,
      replyingToAuthorName: reply.replyingToAuthorName,
    };
  }

  private loadReplies({ append }: { append: boolean }): void {
    if (this.isLoadingReplies) return;

    this.isLoadingReplies = true;
    this.repliesPage = append ? this.repliesPage + 1 : 0;

    this.commentService.getFlatReplies(this.comment.id, this.repliesPage, this.repliesPageSize, 'NEWEST').subscribe({
      next: (result) => {
        if (!result.isSuccess) {
          console.error('Error loading replies:', result.error);
          return;
        }

        const loadedReplies = result.value.content.map((reply) => this.toUiComment(reply));
        this.comment.flatReplies = append && this.comment.flatReplies ? [...this.comment.flatReplies, ...loadedReplies] : loadedReplies;
        if (result.value.totalNumberOfElement > 0) {
          this.comment.totalRepliesCount = result.value.totalNumberOfElement;
        }
        this.hasMoreReplies = result.value.currentPageNumber < (result.value.numberOfPages ?? 1) - 1;
      },
      error: (error) => {
        console.error('Error loading replies:', error);
      },
      complete: () => {
        this.isLoadingReplies = false;
      },
    });
  }
}
